// Preferências do user, persistidas por scope (provider+profile).
//
// Cada (provider, perfil) tem seu próprio set de settings. Trocar de
// scope mostra os settings do novo scope; o anterior fica salvo em
// localStorage sob sua key. Coexistência: kids pode ter fontScale: large
// e adult fontScale: normal no mesmo device.
//
// Implementação: zustand sem persist middleware. Em vez disso, usamos
// useSyncExternalStore manual:
//   1. Ouvir mudança de scope via useSessionStore.subscribe
//   2. Quando scope muda, ler localStorage `iptv:settings:${scope}` e
//      hidratar o store com esses valores (ou defaults se ausente).
//   3. Setters gravam no localStorage daquele scope + atualizam store.
//
// Sem scope ativo (logged out): usa defaults em memória, não persiste
// (não há onde gravar — não tem provider).

import type { ChannelQuality } from '@app-iptv/iptv-client'
import { create } from 'zustand'


import { resetTokenCache } from './lib/card-metrics'
import { currentScope } from './scope'
import { useSessionStore } from './store'

export type FontScale = 'small' | 'normal' | 'large' | 'xlarge'

/** Preferência de áudio do usuário. 'auto' = usa default do parceiro
 *  (defaultAudioLanguage do branding). dubbed/subtitled = força essa
 *  opção quando o título tiver siblings disponíveis. */
export type AudioPreference = 'auto' | 'dubbed' | 'subtitled'

/** Multiplicador REAL de `--font-scale` (small .9 / normal 1 / large 1.15 /
 *  xlarge 1.3) mora só em `packages/ui/src/tokens.css`
 *  (`:root[data-font-scale='...']`) — não duplicado aqui. providers.tsx
 *  aplica o ENUM direto via `dataset.fontScale`; quem multiplica é o CSS
 *  (seletor de atributo), não o JS. Ver o comentário grande nesse arquivo. */

/** Subset persistido em localStorage. Setters não fazem parte. */
export interface SettingsData {
  groupChannels: boolean
  dedupeVod: boolean
  showCuratedRails: boolean
  reorganizeCatalog: boolean
  reorganizeChannels: boolean
  qualityOverride: ChannelQuality | null
  autoplayHero: boolean
  autoplayNextEpisode: boolean
  reduceMotion: boolean
  fontScale: FontScale
  audioPreference: AudioPreference
}

interface SettingsState extends SettingsData {
  setGroupChannels(v: boolean): void
  setDedupeVod(v: boolean): void
  setShowCuratedRails(v: boolean): void
  setReorganizeCatalog(v: boolean): void
  setReorganizeChannels(v: boolean): void
  setQualityOverride(q: ChannelQuality | null): void
  setAutoplayHero(v: boolean): void
  setAutoplayNextEpisode(v: boolean): void
  setReduceMotion(v: boolean): void
  setFontScale(s: FontScale): void
  setAudioPreference(p: AudioPreference): void
}

const DEFAULTS: SettingsData = {
  groupChannels: true,
  dedupeVod: true,
  showCuratedRails: true,
  reorganizeCatalog: true,
  reorganizeChannels: true,
  qualityOverride: null,
  autoplayHero: true,
  autoplayNextEpisode: true,
  reduceMotion: false,
  fontScale: 'normal',
  audioPreference: 'auto',
}

const STORAGE_PREFIX = 'iptv:settings:'

function storageKey(scope: string): string {
  return `${STORAGE_PREFIX}${scope}`
}

/** Lê settings persistidos pra um scope. Retorna defaults se ausente OU
 *  parse falhar. Merge com defaults pra cobrir campos novos em release. */
function readFromStorage(scope: string): SettingsData {
  if (typeof localStorage === 'undefined') return { ...DEFAULTS }
  try {
    const raw = localStorage.getItem(storageKey(scope))
    if (!raw) return { ...DEFAULTS }
    const parsed = JSON.parse(raw) as Partial<SettingsData>
    return { ...DEFAULTS, ...parsed }
  } catch {
    return { ...DEFAULTS }
  }
}

function writeToStorage(scope: string, data: SettingsData): void {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(storageKey(scope), JSON.stringify(data))
  } catch {
    // quota cheia ou storage indisponível — silencioso (settings não é crítico)
  }
}

/** Estado inicial: lê do scope atual (se houver) ou usa defaults. */
function initialState(): SettingsData {
  const scope = currentScope()
  return scope ? readFromStorage(scope) : { ...DEFAULTS }
}

/**
 * Aplica `--font-scale` no <html> (via atributo `data-font-scale` — quem
 * multiplica é o CSS, ver tokens.css; NÃO `style.setProperty` numérico, que é
 * sintaxe morta no build LEGACY de TV/Chr38, sem custom properties) e invalida
 * o cache de tokens de geometria.
 *
 * ===== POR QUE AQUI (e não só num useEffect de providers.tsx) =====
 * A altura da caixa de info do VodCard NÃO é um px cravado: `--card-vod-info-h`
 * DERIVA de `--font-card-title` × `--font-scale` (index.css). O JS lê esse token
 * pra dimensionar a célula do react-window (`readVodCell`), e lê no RENDER
 * (useMemo do drill). Se o --font-scale só fosse escrito num useEffect, um
 * cold-load direto num drill (deep-link/reload em /movies) com fonte `large`/
 * `xlarge` renderizaria a grade com a altura da fonte NORMAL — célula curta,
 * título estourando, LINHAS SOBREPOSTAS — e não recalcularia nunca (o useMemo
 * já rodou). Escrevendo no MÓDULO (importado no boot, antes do 1º render), o
 * token já está no DOM quando o primeiro componente mede. A troca pra atributo
 * (em vez de inline style) não muda esse timing: é mutação DOM síncrona igual
 * (CSSOM não diferencia os dois — ver o comentário grande em tokens.css).
 *
 * `resetTokenCache()` junto: `--card-vod-info-h` é o único token de geometria que
 * muda em runtime, então é exatamente aqui que o cache do readPxToken tem que
 * cair (ver lib/card-metrics.ts).
 */
export function applyFontScale(scale: FontScale): void {
  if (typeof document === 'undefined') return
  document.documentElement.dataset['fontScale'] = scale
  resetTokenCache()
}

export const useSettingsStore = create<SettingsState>()((set, get) => {
  /** Helper de update: aplica patch + persiste no scope atual. */
  const update = (patch: Partial<SettingsData>): void => {
    set(patch)
    // Fonte mudou → o token que a caixa do card deriva mudou. Aplica ANTES de
    // qualquer re-render consumir a geometria (o useEffect de providers.tsx
    // rodaria tarde demais pro useMemo do drill — ver applyFontScale).
    if (patch.fontScale) applyFontScale(patch.fontScale)
    const scope = currentScope()
    if (!scope) return // logged out — não persiste
    const { groupChannels, dedupeVod, showCuratedRails, reorganizeCatalog,
      reorganizeChannels, qualityOverride, autoplayHero, autoplayNextEpisode,
      reduceMotion, fontScale, audioPreference } = get()
    writeToStorage(scope, {
      groupChannels, dedupeVod, showCuratedRails, reorganizeCatalog,
      reorganizeChannels, qualityOverride, autoplayHero, autoplayNextEpisode,
      reduceMotion, fontScale, audioPreference,
    })
  }

  return {
    ...initialState(),
    setGroupChannels: (v) => update({ groupChannels: v }),
    setDedupeVod: (v) => update({ dedupeVod: v }),
    setShowCuratedRails: (v) => update({ showCuratedRails: v }),
    setReorganizeCatalog: (v) => update({ reorganizeCatalog: v }),
    setReorganizeChannels: (v) => update({ reorganizeChannels: v }),
    setQualityOverride: (q) => update({ qualityOverride: q }),
    setAutoplayHero: (v) => update({ autoplayHero: v }),
    setAutoplayNextEpisode: (v) => update({ autoplayNextEpisode: v }),
    setReduceMotion: (v) => update({ reduceMotion: v }),
    setFontScale: (s) => update({ fontScale: s }),
    setAudioPreference: (p) => update({ audioPreference: p }),
  }
})

// Aplica o --font-scale do estado inicial JÁ NO BOOT (este módulo é importado por
// providers.tsx, logo roda antes do 1º render). É o que garante que um cold-load
// direto num drill com fonte large/xlarge nasça com a célula do grid na altura
// CERTA — ver applyFontScale.
applyFontScale(useSettingsStore.getState().fontScale)

/** Reage a mudança de scope (provider OU perfil) — recarrega settings
 *  do novo scope, sem perder os do anterior (ficam em localStorage sob
 *  outra key). Subscribe vivo durante toda a sessão.
 *
 *  Idempotente: chamadas repetidas não duplicam listeners. */
let _scopeSyncRegistered = false
export function registerSettingsScopeSync(): void {
  if (_scopeSyncRegistered) return
  _scopeSyncRegistered = true

  let lastScope = currentScope()
  useSessionStore.subscribe((state, prev) => {
    // Só recomputa scope quando provider OU perfil ativo mudam — evita
    // trabalho em mudanças irrelevantes (branding, accountExpiresAt, etc).
    if (
      state.providerCode === prev.providerCode &&
      state.direct?.listName === prev.direct?.listName &&
      state.activeProfileId === prev.activeProfileId
    ) {
      return
    }
    const newScope = currentScope()
    if (newScope === lastScope) return
    lastScope = newScope
    if (!newScope) {
      // Logged out — restaura defaults em memória (não persiste)
      useSettingsStore.setState({ ...DEFAULTS })
      applyFontScale(DEFAULTS.fontScale)
      return
    }
    // Hidrata com o que está salvo pro novo scope (ou defaults se primeira
    // vez nesse scope). Setters NÃO são parte do data — só sobrescreve campos.
    const hydrated = readFromStorage(newScope)
    useSettingsStore.setState(hydrated)
    // O scope trouxe outra fonte (kids em `large`, adulto em `normal`): o token
    // de geometria muda junto. Sem isto, a grade do novo perfil manteria a
    // altura de célula do perfil anterior.
    applyFontScale(hydrated.fontScale)
  })
}
